import { Add } from "iconsax-react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CMSFilterBar } from "../components/CMSFilterBar";
import { CMSPagination } from "../components/CMSPagination";
import { CMSTable } from "../components/CMSTable";
import { CMSSheet } from "../components/CMSSheet";
import { TreatmentForm } from "../components/TreatmentForm";
import { DeleteConfirmModal } from "../components/DeleteConfirmModal";
import { DateFilterModal } from "../components/DateFilterModal";
import instance, { url } from "../../../../axios/instance";

export default function TreatmentsPage() {
  const [treatments, setTreatments] = useState([]);
  const [categories, setCategories] = useState(["All Categories"]);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const itemsPerPage = 10;
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    treatment: null,
  });
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    slug: "",
    price: "",
    duration: 0,
    shortDescription: "",
    heroDescription: "",
    aboutTreatment: "",
    safetyInformation: "",
    heroImage: null,
    benefitImage: null,
    benefits: [{ title: "", description: "" }],
    faqs: [{ question: "", answer: "" }],
  });

  const statuses = ["All Status", "published", "draft", "archived"];
  const token = window.sessionStorage.getItem("ACCESS_TOKEN");

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchTreatments();
  }, [searchQuery, selectedCategory, selectedStatus, dateRange, pageIndex]);

  const fetchCategories = async () => {
    try {
      const response = await instance.get("/doctor/cms/categories", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const categoryNames = response.data.map((cat) => cat.name);
      setCategories(["All Categories", ...categoryNames]);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    }
  };

  const fetchTreatments = async () => {
    setLoading(true);
    try {
      const params = {
        page: pageIndex,
        limit: itemsPerPage,
      };

      if (searchQuery) params.search = searchQuery;
      if (selectedCategory !== "All Categories")
        params.category = selectedCategory;
      if (selectedStatus !== "All Status") params.status = selectedStatus;
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      const response = await instance.get("/doctor/cms/treatments", {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      setTreatments(response.data.data);
      setTotalItems(response.data.total);
    } catch (error) {
      console.error("Error fetching treatments:", error);
      toast.error("Failed to load treatments");
      setTreatments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, treatment) => {
    setOpenDropdown(null);

    switch (action) {
      case "publish":
        await handleStatusChange(treatment.id, "published");
        break;
      case "unpublish":
        await handleStatusChange(treatment.id, "draft");
        break;
      case "edit":
        handleEditTreatment(treatment);
        break;
      case "delete":
        setDeleteModal({ isOpen: true, treatment });
        break;
      default:
        break;
    }
  };

  const handleStatusChange = async (id, status) => {
    setLoading(true);
    try {
      await instance.put(
        `/doctor/cms/treatments/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      await fetchTreatments();
      toast.success(`Treatment status updated to ${status}`);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update treatment status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSheet = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData({
      name: "",
      category: "",
      slug: "",
      price: "",
      duration: 0,
      shortDescription: "",
      heroDescription: "",
      aboutTreatment: "",
      safetyInformation: "",
      heroImage: null,
      benefitImage: null,
      benefits: [{ title: "", description: "" }],
      faqs: [{ question: "", answer: "" }],
    });
    setIsSheetOpen(true);
  };

  const handleEditTreatment = (treatment) => {
    setIsEditing(true);
    setEditingId(treatment.id);

    let parsedBenefits = [{ title: "", description: "" }];
    let parsedFaqs = [{ question: "", answer: "" }];

    try {
      parsedBenefits =
        typeof treatment.benefits === "string"
          ? JSON.parse(treatment.benefits)
          : treatment.benefits || [{ title: "", description: "" }];
    } catch (e) {
      console.error("Error parsing benefits:", e);
    }

    try {
      const rawFaqs =
        typeof treatment.faqs === "string"
          ? JSON.parse(treatment.faqs)
          : treatment.faqs || [{ question: "", answer: "" }];

      parsedFaqs = rawFaqs.map((faq) => ({
        question: faq.question || faq.title || "",
        answer: faq.answer || faq.description || "",
      }));
    } catch (e) {
      console.error("Error parsing FAQs:", e);
    }

    const getFullImageUrl = (path) => {
      if (!path) return null;
      if (path.startsWith("http://") || path.startsWith("https://")) {
        return path;
      }

      const cleanPath = path.startsWith("/") ? path : `/${path}`;
      return `${url}${cleanPath}`;
    };

    setFormData({
      name: treatment.name || "",
      category: treatment.category || "",
      slug: treatment.slug || "",
      price: treatment.price || "",
      duration: treatment.duration ?? 0,
      shortDescription: treatment.short_description || "",
      heroDescription: treatment.hero_description || "",
      aboutTreatment: treatment.about_treatment || "",
      safetyInformation: treatment.safety_information || "",
      heroImage: treatment.hero_image_path
        ? {
          preview: getFullImageUrl(treatment.hero_image_path),
          name: "Current image",
        }
        : null,
      benefitImage: treatment.benefit_image_path
        ? {
          preview: getFullImageUrl(treatment.benefit_image_path),
          name: "Current image",
        }
        : null,
      benefits: parsedBenefits,
      faqs: parsedFaqs,
    });
    setIsSheetOpen(true);
  };

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    setIsEditing(false);
    setEditingId(null);
  };

  const handleSaveTreatment = async () => {
    setLoading(true);

    try {
      const formDataToSend = new FormData();

      const dataObject = {
        name: formData.name,
        category: formData.category,
        slug: formData.slug,
        price: formData.price,
        duration: formData.duration ?? 0,
        shortDescription: formData.shortDescription,
        heroDescription: formData.heroDescription,
        aboutTreatment: formData.aboutTreatment,
        safetyInformation: formData.safetyInformation,
        benefits: formData.benefits,
        faqs: formData.faqs,
      };

      formDataToSend.append("formData", JSON.stringify(dataObject));

      if (formData.heroImage) {
        formDataToSend.append("heroImage", formData.heroImage.file);
      }
      if (formData.benefitImage) {
        formDataToSend.append("benefitImage", formData.benefitImage.file);
      }

      if (isEditing) {
        await instance.put(
          `/doctor/cms/treatments/${editingId}`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );
      } else {
        await instance.post("/doctor/cms/treatments", formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      }

      setFormData({
        name: "",
        category: "",
        slug: "",
        price: "",
        duration: 0,
        shortDescription: "",
        heroDescription: "",
        aboutTreatment: "",
        safetyInformation: "",
        heroImage: null,
        benefitImage: null,
        benefits: [{ title: "", description: "" }],
        faqs: [{ question: "", answer: "" }],
      });

      setIsSheetOpen(false);
      setIsEditing(false);
      setEditingId(null);
      await fetchTreatments();
      toast.success(
        isEditing
          ? "Treatment updated successfully!"
          : "Treatment created successfully!",
      );
    } catch (error) {
      console.error("Error saving treatment:", error);
      toast.error(
        error.response?.data?.message ||
        "Failed to save treatment. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);

    try {
      const treatment = deleteModal.treatment;

      if (treatment.status === "archived") {
        await instance.delete(
          `/doctor/cms/treatments/${treatment.id}/permanent`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        await instance.delete(`/doctor/cms/treatments/${treatment.id}/soft`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      setDeleteModal({ isOpen: false, treatment: null });
      await fetchTreatments();
      toast.success(
        treatment.status === "archived"
          ? "Treatment permanently deleted!"
          : "Treatment archived successfully!",
      );
    } catch (error) {
      console.error("Error deleting treatment:", error);
      toast.error(
        error.response?.data?.message ||
        "Failed to delete treatment. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    formData.name &&
    formData.category &&
    formData.slug &&
    formData.price &&
    formData.shortDescription &&
    formData.heroDescription &&
    formData.aboutTreatment &&
    (!isEditing || formData.heroImage || formData.benefitImage !== null) &&
    (isEditing || (formData.heroImage && formData.benefitImage));

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const columns = [
    { key: "name", label: "Name", align: "left" },
    { key: "category", label: "Category", align: "left" },
    { key: "price", label: "Price", align: "left" },
    { key: "status", label: "Status", align: "left" },
    { key: "updated", label: "Updated", align: "left" },
    { key: "actions", label: "", align: "center" },
  ];

  const actions = [
    {
      key: "publish",
      label: "Publish",
      variant: "success",
      condition: (item) => item.status !== "published",
    },
    {
      key: "unpublish",
      label: "Unpublish",
      variant: "warning",
      condition: (item) => item.status === "published",
    },
    {
      key: "edit",
      label: "Edit",
      variant: "primary",
    },
    {
      key: "delete",
      label: (treatment) =>
        treatment.status === "archived" ? "Delete Permanently" : "Archive",
      variant: "danger",
    },
  ];

  const renderCell = (key, item) => {
    switch (key) {
      case "name":
        return (
          <div className="text-[14px] font-medium text-gray-900">
            {item.name}
          </div>
        );
      case "category":
        return <div className="text-[14px] text-gray-600">{item.category}</div>;
      case "price":
        return (
          <div className="text-[14px] font-medium text-gray-900">
            ${parseFloat(item.price).toFixed(2)}
          </div>
        );
      case "status":
        return (
          <span
            className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium capitalize ${item.status === "published"
                ? "bg-green-100 text-green-800"
                : item.status === "draft"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-800"
              }`}
          >
            {item.status}
          </span>
        );
      case "updated":
        return (
          <div className="text-[14px] text-gray-600">
            {formatDate(item.updated_at)}
          </div>
        );
      default:
        return <div className="text-[14px] text-gray-900">{item[key]}</div>;
    }
  };

  return (
    <div className="bg-[#fafafa] min-h-screen">
      <div className="max-w-[100%] w-full px-4 lg:px-7 py-5">
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start mb-2">
            <div className="mb-2 lg:mb-0">
              <h1 className="text-2xl font-bold text-gray-900">
                Treatment Pages
              </h1>
              <p className="text-gray-600 text-[14px] mt-1 max-w-2xl">
                Manage all treatment pages on your website. Click any item to
                edit details, update content, or publish new treatments.
              </p>
            </div>
            <button
              onClick={handleOpenSheet}
              className="bg-primary py-2.5 px-4 rounded-md font-medium text-[14px] hover:opacity-80 text-white flex items-center gap-2 transition-all duration-300"
            >
              <Add size={18} />
              New Page
            </button>
          </div>
        </div>

        <CMSFilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search treatments..."
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          statuses={statuses}
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          showDateFilter={true}
          onDateClick={() => setIsDateFilterOpen(true)}
        />

        <CMSTable
          columns={columns}
          data={treatments}
          loading={loading}
          emptyMessage="No treatments found"
          onActionClick={handleAction}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          actions={actions}
          renderCell={renderCell}
        />

        {!loading && (
          <CMSPagination
            currentPage={pageIndex}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setPageIndex}
            itemName="treatments"
            showingCount={treatments.length}
          />
        )}
      </div>

      <CMSSheet
        isOpen={isSheetOpen}
        onClose={handleCloseSheet}
        onCancel={handleCloseSheet}
        onSave={handleSaveTreatment}
        title={isEditing ? "Edit Treatment page" : "Treatment Pages"}
        desc={
          "Manage all treatment pages on your website. Click any item to edit details, update content, or publish new treatments."
        }
        saveButtonText={isEditing ? "Update" : "Create"}
        cancelButtonText="Cancel"
        isLoading={loading}
        isSaveDisabled={!isFormValid}
      >
        <TreatmentForm
          formData={formData}
          onChange={setFormData}
          categories={categories.filter((c) => c !== "All Categories")}
          isEditing={isEditing}
        />
      </CMSSheet>

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, treatment: null })}
        onConfirm={handleDeleteConfirm}
        itemName={deleteModal.treatment?.name}
        isLoading={loading}
        message={
          deleteModal.treatment?.status === "archived"
            ? "This will permanently delete the treatment. This action cannot be undone."
            : "This will archive the treatment. You can permanently delete it later from the archived items."
        }
      />

      <DateFilterModal
        isOpen={isDateFilterOpen}
        onClose={() => setIsDateFilterOpen(false)}
        onApply={() => setIsDateFilterOpen(false)}
        dateRange={dateRange}
        setDateRange={setDateRange}
      />
    </div>
  );
}
